import type { Asset } from "./schema";

export const runtimeStageAssetIdBySceneId = {
  M2S1: "AST-M2S1-RUNTIME-STAGE",
  M2S2: "AST-M2S2-RUNTIME-STAGE",
  M2S3: "AST-M2S3-RUNTIME-STAGE",
  M3S1: "AST-M3S1-RUNTIME-STAGE",
  M3S2: "AST-M3S2-RUNTIME-STAGE",
  M3S3: "AST-M3S3-RUNTIME-STAGE",
  M3S4: "AST-M3S4-RUNTIME-STAGE",
  M4S1: "AST-M4S1-RUNTIME-STAGE",
  M4S2: "AST-M4S2-RUNTIME-STAGE",
  M4S3: "AST-M4S3-RUNTIME-STAGE",
  M5S1: "AST-M5S1-RUNTIME-STAGE",
  M5S2: "AST-M5S2-RUNTIME-STAGE",
  M5S3: "AST-M5S3-RUNTIME-STAGE",
  M6S1: "AST-M6S1-RUNTIME-STAGE",
  M6S2: "AST-M6S2-RUNTIME-STAGE",
  M6S3: "AST-M6S3-RUNTIME-STAGE",
} as const;

export type RuntimeStageSceneId = keyof typeof runtimeStageAssetIdBySceneId;
export type RuntimeStageAssetId = (typeof runtimeStageAssetIdBySceneId)[RuntimeStageSceneId];

export function runtimeStageAssetIdForScene(sceneId: string): RuntimeStageAssetId {
  const assetId = runtimeStageAssetIdBySceneId[sceneId as RuntimeStageSceneId];
  if (!assetId) throw new Error(`${sceneId}未登记统一运行舞台资产。`);
  return assetId;
}

const m1s1AssetSeeds = [
  ["AST-M1S1-PROC-001", "卢沟桥：全民族抗战爆发", "卢沟桥及宛平城夜景的全屏历史复原空间。"],
  [
    "AST-M1S1-PROC-002",
    "通电全国：只有全民族抗战",
    "以历史环境、人物与电讯物件热点呈现紧急通电时间节点的全屏复原空间。",
  ],
  [
    "AST-M1S1-PROC-003",
    "两份公开文件：合作正式形成",
    "以历史环境与文件热点呈现1937年9月两份公开文件相继发表的全屏复原空间。",
  ],
  [
    "AST-M1S1-PROC-004",
    "改编档案：共同抗战实践",
    "以历史环境、人物与物件热点梳理八路军、新四军改编关系的全屏复原空间。",
  ],
  [
    "AST-M1S1-PROC-005",
    "团结就是力量",
    "以历史环境、人物与物件热点呈现抗日民族统一战线汇聚各界力量的全屏复原空间。",
  ],
  [
    "AST-M1S1-PROC-006",
    "广西学生军",
    "以广西学生军出发历史环境和人物/物件热点承载地方史拓展的全屏复原空间。",
  ],
] as const;

type M1S1AssetId = (typeof m1s1AssetSeeds)[number][0];

const m1s1ImmersiveFileRefs = [
  "src/apps/learner-web/src/components/SceneExperience.tsx",
  "src/apps/learner-web/src/components/RuntimeImmersiveStage.tsx",
  "src/apps/learner-web/src/components/immersiveSceneConfigs.ts",
  "src/apps/learner-web/src/assets/immersive/source/provenance.json",
] as const;

const m1s1AssetFileRefs: Readonly<Record<M1S1AssetId, readonly string[]>> = {
  "AST-M1S1-PROC-001": m1s1ImmersiveFileRefs,
  "AST-M1S1-PROC-002": m1s1ImmersiveFileRefs,
  "AST-M1S1-PROC-003": m1s1ImmersiveFileRefs,
  "AST-M1S1-PROC-004": m1s1ImmersiveFileRefs,
  "AST-M1S1-PROC-005": m1s1ImmersiveFileRefs,
  "AST-M1S1-PROC-006": m1s1ImmersiveFileRefs,
};

const m1s1Assets: Asset[] = m1s1AssetSeeds.map(([id, title, altZh]) => ({
  id,
  title,
  sceneIds: ["M1S1"],
  kind: "media",
  fileRefs: [...m1s1AssetFileRefs[id]],
  sourceRefs:
    id === "AST-M1S1-PROC-006"
      ? ["SRC_GX_STUDENT_ARMY"]
      : id === "AST-M1S1-PROC-005"
        ? ["SRC_TEXTBOOK_CH3", "SRC_OWNER_C03"]
        : ["SRC_TEXTBOOK_CH3"],
  rightsStatus: "original",
  originality: "ai_assisted",
  altZh,
  restorativeCreation: true,
  visibleLabel: "复原性创作·非档案影像",
  reviewState: "pending_review",
}));

const runtimeStageAssetSeeds = [
  ["M2S1", "平型关前线沉浸历史空间", "以平型关前线历史环境与证据热点呈现战斗及其历史意义。"],
  [
    "M2S2",
    "敌后根据地沉浸历史空间",
    "以晋察冀敌后根据地复原空间呈现根据地创建、民主政权和人民支持。",
  ],
  ["M2S3", "独立自主原则沉浸历史空间", "以历史会议环境与证据热点呈现统一战线中独立自主原则。"],
  ["M3S1", "相持阶段局势沉浸历史空间", "以抗战相持阶段历史环境与证据热点呈现形势变化和方针关系。"],
  [
    "M3S2",
    "敌后游击战争与广西地方抗日武装沉浸历史空间",
    "以华北铁路和广西地方抗日动员复原空间呈现敌后游击战争发展，不提供可操控军事路线。",
  ],
  [
    "M3S3",
    "沦陷区抗日斗争沉浸历史空间",
    "以克制的城市地下工作环境与证据热点呈现沦陷区抗日斗争，不使用创伤性影像。",
  ],
  [
    "M3S4",
    "反共高潮与政治应对沉浸历史空间",
    "以历史办公环境、文件与人物热点呈现反共高潮及中国共产党的政治应对。",
  ],
  [
    "M4S1",
    "根据地反扫荡反清乡沉浸历史空间",
    "以克制的根据地历史环境与证据热点呈现反扫荡、反清乡斗争。",
  ],
  [
    "M4S2",
    "南泥湾大生产沉浸历史空间",
    "以南泥湾生产现场与证据热点呈现大生产运动、根据地建设和延安精神。",
  ],
  [
    "M4S3",
    "抗日民主运动、桂林八办与桂林文化沉浸历史空间",
    "以重庆民主运动、桂林八办和桂林文化城复原空间串联教材主线及广西地方史拓展。",
  ],
  [
    "M5S1",
    "党的建设伟大工程沉浸历史空间",
    "以延安学习环境与证据热点呈现组织发展、文件和建设要求。",
  ],
  [
    "M5S2",
    "新民主主义理论研学沉浸历史空间",
    "以理论研学历史环境与证据热点呈现社会性质、革命道路和理论命题。",
  ],
  [
    "M5S3",
    "整风运动与历史决议沉浸历史空间",
    "以整风学习与文艺座谈连续历史空间呈现时间、文件和思想方法关系。",
  ],
  [
    "M6S1",
    "党的七大沉浸历史空间",
    "以党的七大会场复原空间与证据热点呈现会议议程、报告和政治路线。",
  ],
  [
    "M6S2",
    "指导思想确立沉浸历史空间",
    "以历史学习环境与证据热点呈现党章、报告和指导思想确立的理论关系。",
  ],
  [
    "M6S3",
    "全面反攻与抗战胜利沉浸历史空间",
    "以反攻指挥环境与胜利纪念空间呈现全面反攻及抗日战争胜利的历史节点。",
  ],
] as const satisfies ReadonlyArray<readonly [RuntimeStageSceneId, string, string]>;

const runtimeStageFileRefs = [
  "src/apps/learner-web/src/components/RuntimeImmersiveStage.tsx",
  "src/apps/learner-web/src/components/RuntimeImmersiveStage.module.css",
  "src/apps/learner-web/src/components/immersiveSceneConfigs.ts",
  "src/apps/learner-web/src/components/RuntimeSceneExperience.tsx",
  "src/apps/learner-web/src/components/RuntimeSceneExperience.module.css",
  "src/apps/learner-web/src/assets/immersive/source/provenance.json",
  "src/packages/content/src/runtimeSceneBlueprints.ts",
] as const;

const runtimeStageAssets: Asset[] = runtimeStageAssetSeeds.map(([sceneId, title, altZh]) => ({
  id: runtimeStageAssetIdBySceneId[sceneId],
  title,
  sceneIds: [sceneId],
  kind: "media",
  fileRefs: [...runtimeStageFileRefs],
  sourceRefs:
    sceneId === "M3S2"
      ? ["SRC_TEXTBOOK_CH3", "SRC_GX_WAR_HISTORY", "SRC_GX_PARTY_HISTORY_2020"]
      : sceneId === "M4S3"
        ? [
            "SRC_TEXTBOOK_CH3",
            "SRC_GX_PARTY_HISTORY_2020",
            "SRC_GX_WAR_HISTORY",
            "SRC_GUILIN_CITY_HISTORY",
          ]
        : ["SRC_TEXTBOOK_CH3"],
  rightsStatus: "original",
  originality: "ai_assisted",
  altZh,
  restorativeCreation: true,
  visibleLabel: "复原性创作·非档案影像",
  reviewState: "pending_review",
}));

export const assets = [
  ...m1s1Assets,
  {
    id: "AST-M1S2-CONFERENCE-TABLE",
    title: "洛川会议全屏历史复原空间",
    sceneIds: ["M1S2"],
    kind: "media",
    fileRefs: [
      "src/apps/learner-web/src/components/M1S2ImmersiveStage.tsx",
      "src/apps/learner-web/src/components/M1S2Experience.tsx",
      "src/apps/learner-web/src/assets/immersive/source/provenance.json",
    ],
    sourceRefs: ["SRC_TEXTBOOK_CH3"],
    rightsStatus: "original",
    originality: "ai_assisted",
    altZh: "洛川会议历史环境复原空间；人物面貌、动作和座次不作为史实依据。",
    restorativeCreation: true,
    visibleLabel: "复原性创作·非档案影像",
    reviewState: "pending_review",
  },
  {
    id: "AST-M1S2-MAP-TABLE",
    title: "《论持久战》讲演全屏历史复原空间",
    sceneIds: ["M1S2"],
    kind: "media",
    fileRefs: [
      "src/apps/learner-web/src/components/M1S2ImmersiveStage.tsx",
      "src/apps/learner-web/src/components/M1S2Experience.tsx",
      "src/apps/learner-web/src/assets/immersive/source/provenance.json",
    ],
    sourceRefs: ["SRC_TEXTBOOK_CH3"],
    rightsStatus: "original",
    originality: "ai_assisted",
    altZh: "《论持久战》讲演历史环境复原空间；人物面貌、动作和座次不作为史实依据。",
    restorativeCreation: true,
    visibleLabel: "复原性创作·非档案影像",
    reviewState: "pending_review",
  },
  {
    id: "AST-M1S2-DOCUMENT-WALL",
    title: "《论持久战》研读全屏历史复原空间",
    sceneIds: ["M1S2"],
    kind: "media",
    fileRefs: [
      "src/apps/learner-web/src/components/M1S2ImmersiveStage.tsx",
      "src/apps/learner-web/src/components/M1S2Experience.tsx",
      "src/apps/learner-web/src/assets/immersive/source/provenance.json",
    ],
    sourceRefs: ["SRC_TEXTBOOK_CH3"],
    rightsStatus: "original",
    originality: "ai_assisted",
    altZh: "《论持久战》讲演与研读历史环境复原空间；文件外观不作为原件依据。",
    restorativeCreation: true,
    visibleLabel: "复原性创作·非档案影像",
    reviewState: "pending_review",
  },
  ...runtimeStageAssets,
] satisfies Asset[];
